import { JobHistoryVersionResponseModel } from "../Models/API/JobModel"
import { FetchState, hasValue, getValue } from "../Models/Client/FetchState"
import { useEffect, useMemo } from "react"
import { useDereference } from "./useDereference"
import { Result } from "../Models/Utility/Result"
import { DataAccess } from "../Models/Client/DataAccess"
import { useJobHistory } from "./useJobHistory"
import { useDispatch } from "react-redux"
import { getJobHistoryVersionResponse } from "../Actions/API/Job/JobActions"
import { useAppState } from "./useReduxState"

export function useJobVersionResponse(
    appID: string,
    jobID: string | undefined,
    versionIndex: number
): FetchState<JobHistoryVersionResponseModel> {
    const dispatch = useDispatch()
    const jobHistoryFetch = useJobHistory(jobID)
    const jobVersion =
        versionIndex > 0
            ? getValue(jobHistoryFetch)?.history[versionIndex - 1]
            : undefined
    const jobVersionIndex =
        jobVersion === undefined ? -1 : parseInt(jobVersion.version)
    const jobHistoryVersionResponse = getValue(
        useAppState(state => {
            if (jobID === undefined || jobVersion === undefined) {
                return
            }

            return state.api.jobs.historyVersionResponse[jobID]?.[
                jobVersionIndex
            ]
        })
    )

    const dataAccess = useMemo(() => {
        if (jobVersion !== undefined && jobVersion.jobId !== null) {
            return DataAccess.perJob(jobVersion.jobId.toString())
        }
        return DataAccess.owner
    }, [jobVersion])

    useEffect(() => {
        if (
            jobID !== undefined &&
            jobVersion !== undefined &&
            versionIndex > 0 &&
            jobHistoryVersionResponse?.response === undefined
        ) {
            dispatch(
                getJobHistoryVersionResponse(appID, jobID, jobVersionIndex)
            )
        }
    }, [
        appID,
        dispatch,
        jobHistoryVersionResponse,
        jobID,
        jobVersion,
        jobVersionIndex,
        versionIndex
    ])

    const responseFetch = useDereference(
        versionIndex > 0 ? jobHistoryVersionResponse : undefined,
        dataAccess
    )

    return useMemo(() => {
        if (!hasValue(responseFetch)) {
            return responseFetch
        }

        return Result.ok({
            response: (responseFetch.value as JobHistoryVersionResponseModel)
                .response,
            responseData: (
                responseFetch.value as JobHistoryVersionResponseModel
            ).responseData
        })
    }, [responseFetch])
}
